// Per-session cap on voice web searches — issue #887.
//
// ── Why this is client-side, and why that's honest ───────────────────────────
//
// Same structural reason the capture gate is (voiceCaptureGate.ts, #684):
// kin-voice-session mints an ephemeral token and exits, so no Kin server sees
// the live session and there is nothing server-side that knows "this is the
// fourth search of this call" (#725). kin-search is stateless per request.
//
// So this bounds the COMMON case — the model looping on searches inside one
// conversation, which is the realistic way a voice call runs up a bill. It does
// NOT bound a determined caller hitting kin-search directly; that endpoint is
// Pro-gated and rate-limited by its own cost ceiling, and a Pro user spending
// her own entitlement on searches is not the threat model.
//
// If a real server-side budget is ever needed, it belongs next to the
// entitlement read in kin-search, keyed on user + day — not here. Say so rather
// than letting this read as stronger than it is.
//
// ── Why a cap at all ─────────────────────────────────────────────────────────
//
// Every search bills Anthropic on the same workspace budget CI runs on, which
// has taken Kin down once (CLAUDE.md, ANTHROPIC API BUDGET). A voice session is
// capped at 10 minutes, and a model that decides to search on every turn could
// fire dozens. Six is generous for one conversation and cheap to raise.

export const MAX_SEARCHES_PER_SESSION = 6;

export interface VoiceSearchCapState {
  used: number;
}

export const INITIAL_SEARCH_CAP: VoiceSearchCapState = { used: 0 };

/**
 * ⚠️ `reason?: undefined` on the allowed:true member is load-bearing (#1360).
 *
 * This repo compiles with `strictNullChecks: false`, under which a
 * `{allowed:true}|{allowed:false}` union does NOT narrow on `if (!d.allowed)`
 * — the trap `src/lib/partyPersistence.ts:52-56` documents. Without this,
 * every `d.reason` read is a TS2339 even inside the guard. Declaring the
 * absent member as `undefined` keeps `reason` readable on the un-narrowed
 * union while still making "session_cap" the only value it can ever hold.
 */
export type VoiceSearchDecision =
  | { allowed: true; nextState: VoiceSearchCapState; reason?: undefined }
  | { allowed: false; nextState: VoiceSearchCapState; reason: "session_cap" };

/**
 * Decide whether one more search may run in this session.
 *
 * ⚠️ Counts ATTEMPTS, not successes. A failed search still cost an Anthropic
 * call, and a model that retries a failing query is exactly the loop this cap
 * exists to stop — crediting failures back would make the cap unreachable in
 * the one case it matters.
 */
export function decideVoiceSearch(state: VoiceSearchCapState): VoiceSearchDecision {
  if (state.used >= MAX_SEARCHES_PER_SESSION) {
    return { allowed: false, nextState: state, reason: "session_cap" };
  }
  return { allowed: true, nextState: { used: state.used + 1 } };
}

/**
 * What the model is told when the cap is hit. A tool result, not an error —
 * the session keeps going and Kin explains it in her own words rather than the
 * call breaking. Same posture as the capture gate's rejection string.
 */
export const SEARCH_CAP_TOOL_RESULT =
  "You've looked things up several times this call already. Tell her you've hit your lookup limit for this conversation and she can text you the question instead — then carry on normally.";

// ⚠️ Client-side backstop timeout, deliberately LONGER than kin-search's own
// 12s Anthropic timeout.
//
// The failure this prevents is the worst one available here: if the fetch never
// settles, sendFunctionResult never fires, the realtime model waits forever for
// a function_call_output that is not coming, and Kin goes silent mid-call with
// no error anywhere. Silence is exactly how a voice assistant reads as broken —
// it is the single biggest risk in this feature.
//
// Longer than the server's, on purpose: when the server times out it returns an
// empty result and the client speaks the graceful "couldn't find that" line, and
// we want that to win the race. This fires only when the response never arrives
// at all — a dropped connection, a hung edge runtime.
export const SEARCH_CLIENT_TIMEOUT_MS = 15000;

/** Spoken when the request never came back at all. Honest, not technical. */
export const SEARCH_TIMEOUT_TOOL_RESULT =
  "That lookup took too long and timed out. Tell her you couldn't get it just now, and offer to keep going or have her text you the question.";
